import React, { useEffect, useRef, useState } from 'react';
import CyberTooltip from '../common/CyberTooltip';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';

ChartJS.register(ArcElement, Tooltip);

const StatisticsDetailModal = ({ data, type, onClose }) => {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState({ show: false, text: '' });
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const sortedData = [...data].sort((a, b) => b.value - a.value);

  useEffect(() => {
    if (type === 'sentients' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const particles = [];
      const connectionCount = 12;
      let animationFrame;

      // Set canvas size
      const setCanvasSize = () => {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
      };
      setCanvasSize();
      window.addEventListener('resize', setCanvasSize);

      // Create particles
      for (let i = 0; i < 50; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 1,
          speedX: (Math.random() - 0.5) * 2,
          speedY: (Math.random() - 0.5) * 2,
          opacity: Math.random() * 0.5 + 0.5
        });
      }

      const drawNetwork = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw center hub
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const hubSize = 60;
        
        // Draw connections
        for (let i = 0; i < connectionCount; i++) {
          const angle = (i / connectionCount) * Math.PI * 2;
          const length = Math.min(canvas.width, canvas.height) * 0.4;
          const endX = centerX + Math.cos(angle) * length;
          const endY = centerY + Math.sin(angle) * length;
          
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(endX, endY);
          ctx.strokeStyle = 'rgba(51, 255, 51, 0.2)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        // Update and draw particles
        particles.forEach(particle => {
          particle.x += particle.speedX;
          particle.y += particle.speedY;

          // Bounce off edges
          if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
          if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;

          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(51, 255, 51, ${particle.opacity})`;
          ctx.fill();
        });

        // Draw hub
        ctx.beginPath();
        ctx.arc(centerX, centerY, hubSize, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(51, 255, 51, 0.1)';
        ctx.fill();
        ctx.strokeStyle = '#33ff33';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw inner hub
        ctx.beginPath();
        ctx.arc(centerX, centerY, hubSize * 0.7, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(51, 255, 51, 0.5)';
        ctx.stroke();

        animationFrame = requestAnimationFrame(drawNetwork);
      };

      drawNetwork();

      return () => {
        window.removeEventListener('resize', setCanvasSize);
        cancelAnimationFrame(animationFrame);
      };
    }
  }, [type]);

  const chartData = {
    labels: sortedData.map(item => item.username),
    datasets: [
      {
        data: sortedData.map(item => item.value),
        backgroundColor: sortedData.map(item => {
          const percentage = (item.value / total) * 100;
          const r = Math.round(20 + (percentage / 100) * (51 - 20));
          const g = Math.round(100 + (percentage / 100) * (255 - 100));
          const b = Math.round(20 + (percentage / 100) * (51 - 20));
          return `rgba(${r}, ${g}, ${b}, 0.8)`;
        }),
        borderColor: sortedData.map(item => {
          const percentage = (item.value / total) * 100;
          const r = Math.round(20 + (percentage / 100) * (51 - 20));
          const g = Math.round(100 + (percentage / 100) * (255 - 100));
          const b = Math.round(20 + (percentage / 100) * (51 - 20));
          return `rgb(${r}, ${g}, ${b})`;
        }),
        borderWidth: 1,
        hoverOffset: 20
      }
    ]
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleFont: {
          family: 'IBM BIOS',
          size: 14
        },
        bodyFont: {
          family: 'IBM BIOS',
          size: 12
        },
        padding: 12,
        borderColor: '#33ff33',
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const percentage = ((context.raw / total) * 100).toFixed(1);
            const value = formatNumber(context.raw);
            return `${context.label}: ${percentage}% (${value} qubits)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  const renderContent = () => {
    switch (type) {
      case 'qubits':
        return (
          <div style={{
            height: '450px',
            position: 'relative',
            padding: '40px',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(51, 255, 51, 0.3)',
            animation: 'borderGlow 2s infinite ease-in-out'
          }}>
            <Pie data={chartData} options={options} />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none'
            }}>
              <div style={{
                fontSize: '16px',
                color: '#33ff33',
                textShadow: '0 0 5px #33ff33',
                fontFamily: 'IBM BIOS, monospace',
                animation: 'pulse 2s infinite ease-in-out',
                background: 'rgba(0, 0, 0, 0.7)',
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid rgba(51, 255, 51, 0.2)'
              }}>
                TOTAL
                <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                  {formatNumber(total)}
                </div>
              </div>
            </div>
          </div>
        );

      case 'omega3':
        return (
          <div style={{
            padding: '40px',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(51, 255, 51, 0.3)',
            animation: 'borderGlow 2s infinite ease-in-out',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '15px',
              position: 'relative'
            }}>
              {Array.from({ length: 20 }, (_, i) => {
                const user = sortedData[i];
                return (
                  <div
                    key={i}
                    className="slot"
                    onMouseEnter={() => user && setTooltip({
                      show: true,
                      text: user.username
                    })}
                    onMouseLeave={() => setTooltip({ show: false, text: '' })}
                    style={{
                      aspectRatio: '1',
                      background: user ? 'rgba(51, 255, 51, 0.2)' : 'rgba(51, 255, 51, 0.05)',
                      border: `2px solid ${user ? '#33ff33' : 'rgba(51, 255, 51, 0.2)'}`,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'visible'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: user ? 'linear-gradient(transparent 30%, rgba(51, 255, 51, 0.1))' : 'none',
                      animation: user ? 'scanline 2s linear infinite' : 'none',
                      pointerEvents: 'none'
                    }} />
                    <div style={{
                      fontSize: '14px',
                      fontFamily: 'IBM BIOS, monospace',
                      textAlign: 'center',
                      color: '#33ff33',
                      textShadow: '0 0 5px #33ff33',
                      zIndex: 1
                    }}>
                      {`${i + 1}/20`}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{
              textAlign: 'center',
              marginTop: '30px',
              fontSize: '16px',
              color: '#33ff33',
              textShadow: '0 0 5px #33ff33',
              fontFamily: 'IBM BIOS, monospace'
            }}>
              {`${total}/20 SLOTS ACTIVATED`}
            </div>
            <CyberTooltip
              show={tooltip.show}
              text={tooltip.text}
            />
          </div>
        );

      case 'sentients':
        return (
          <div style={{
            height: '450px',
            position: 'relative',
            padding: '40px',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(51, 255, 51, 0.3)',
            animation: 'borderGlow 2s infinite ease-in-out'
          }}>
            <canvas
              ref={canvasRef}
              style={{
                width: '100%',
                height: '100%'
              }}
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 1
            }}>
              <div style={{
                fontSize: '16px',
                color: '#33ff33',
                textShadow: '0 0 5px #33ff33',
                fontFamily: 'IBM BIOS, monospace',
                animation: 'pulse 2s infinite ease-in-out',
                background: 'rgba(0, 0, 0, 0.7)',
                padding: '15px 25px',
                borderRadius: '8px',
                border: '1px solid rgba(51, 255, 51, 0.2)'
              }}>
                 SENTIENTS
                <div style={{
                  fontSize: '32px',
                  marginTop: '10px',
                  letterSpacing: '2px'
                }}>
                  {total}/200
                </div>
              </div>
            </div>
          </div>
        );

      case 'neuralDrift':
        const maxPossibleAnchor = sortedData.length * 100;
        const driftPercent = maxPossibleAnchor > 0 ? Math.round((total / maxPossibleAnchor) * 100) : 0;
        return (
          <div style={{
            padding: '40px',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(51, 255, 51, 0.3)',
            animation: 'borderGlow 2s infinite ease-in-out'
          }}>
            {/* Large progress bar */}
            <div style={{
              marginBottom: '30px',
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              border: '1px solid rgba(51, 255, 51, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px',
                fontFamily: 'IBM BIOS, monospace',
                fontSize: '14px'
              }}>
                <span>NETWORK ANCHOR STRENGTH</span>
                <span style={{ color: '#33ff33', textShadow: '0 0 5px #33ff33' }}>{driftPercent}%</span>
              </div>
              <div style={{
                height: '20px',
                background: 'rgba(51, 255, 51, 0.1)',
                borderRadius: '4px',
                overflow: 'hidden',
                border: '1px solid rgba(51, 255, 51, 0.3)'
              }}>
                <div style={{
                  width: `${driftPercent}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #146414, #33ff33)',
                  boxShadow: '0 0 15px #33ff33',
                  transition: 'width 0.5s ease-out'
                }} />
              </div>
              <div style={{
                marginTop: '10px',
                fontSize: '12px',
                opacity: 0.7,
                fontFamily: 'IBM BIOS, monospace',
                textAlign: 'center'
              }}>
                {total} / {maxPossibleAnchor} TOTAL ANCHOR POINTS
              </div>
            </div>

            {/* Top contributors */}
            <div style={{
              fontSize: '14px',
              marginBottom: '15px',
              fontFamily: 'IBM BIOS, monospace',
              opacity: 0.8
            }}>TOP ANCHOR CONTRIBUTORS</div>
            <div style={{
              maxHeight: '250px',
              overflowY: 'auto',
              paddingRight: '10px'
            }}>
              {sortedData.slice(0, 10).map((player, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px',
                  padding: '10px',
                  background: 'rgba(51, 255, 51, 0.05)',
                  borderRadius: '4px',
                  border: '1px solid rgba(51, 255, 51, 0.2)'
                }}>
                  <div style={{
                    width: '30px',
                    fontFamily: 'IBM BIOS, monospace',
                    fontSize: '12px',
                    opacity: 0.7
                  }}>#{index + 1}</div>
                  <div style={{ flex: 1, fontFamily: 'IBM BIOS, monospace', fontSize: '14px' }}>
                    {player.username}
                  </div>
                  <div style={{
                    width: '100px',
                    marginRight: '10px'
                  }}>
                    <div style={{
                      height: '6px',
                      background: 'rgba(51, 255, 51, 0.1)',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${player.value}%`,
                        height: '100%',
                        background: '#33ff33',
                        boxShadow: '0 0 5px #33ff33'
                      }} />
                    </div>
                  </div>
                  <div style={{
                    width: '50px',
                    textAlign: 'right',
                    fontFamily: 'IBM BIOS, monospace',
                    fontSize: '14px',
                    color: '#33ff33',
                    textShadow: '0 0 5px #33ff33'
                  }}>{player.value}/100</div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 10001,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        cursor: 'pointer'
      }}>
      <div style={{
        background: 'rgba(0, 0, 0, 0.95)',
        border: '2px solid #33ff33',
        borderRadius: '12px',
        padding: '40px',
        width: '700px',
        color: '#33ff33',
        boxShadow: '0 0 30px rgba(51, 255, 51, 0.3)',
        position: 'relative',
        cursor: 'default',
        backgroundImage: `
          linear-gradient(0deg, rgba(51, 255, 51, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(51, 255, 51, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px'
      }}>
        <style>
          {`
            @keyframes borderGlow {
              0% { border-color: #33ff33; }
              50% { border-color: #66ff66; }
              100% { border-color: #33ff33; }
            }
            @keyframes pulse {
              0% { opacity: 0.8; }
              50% { opacity: 1; }
              100% { opacity: 0.8; }
            }
            @keyframes scanline {
              0% { transform: translateY(-100%); }
              100% { transform: translateY(100%); }
            }
            .slot {
              transition: all 0.3s ease;
              position: relative;
            }
            .slot:hover {
              transform: scale(1.1);
              box-shadow: 0 0 20px #33ff33;
              z-index: 2;
            }
          `}
        </style>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            color: '#33ff33',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '5px',
            zIndex: 1
          }}
        >
          ×
        </button>

        <h2 style={{
          textAlign: 'center',
          marginBottom: '30px',
          fontSize: '24px',
          textShadow: '0 0 15px #33ff33',
          fontFamily: 'IBM BIOS, monospace',
          letterSpacing: '2px'
        }}>
          {type === 'qubits' ? 'QUANTUM NETWORK DISTRIBUTION' :
           type === 'omega3' ? 'OMEGA-3 CLEARANCE NODES' :
           type === 'neuralDrift' ? 'NEURAL DRIFT ANALYSIS' :
           'NETWORK SENTIENTS'}
        </h2>

        {renderContent()}
      </div>
    </div>
  );
};

export default StatisticsDetailModal;